import { logger } from '@magicschool/logger';
import { renderMath, segmentDelimitedMath } from '@magicschool/math';
import { textHasMathEquations } from '@/features/tts/preserveAudioUserGesture';
import { _injectScript } from '@/util/dynamicLoader';

/**
 * `\[ ... \]` and `\( ... \)`, the forms model output uses. Converted without a content check: a
 * backslash delimiter is explicit authorship, and the worst case is mild. Escaped prose does slip
 * through (`\[SL.2.1\]` is verbalized "S L .2 .1"), which stays close enough to the literal text to
 * be acceptable. The `$` gate below is stricter because its worst case is not: a stray span there
 * swallows the prose between two dollar amounts.
 */
const BACKSLASH_DISPLAY_MATH = /\\\[([\s\S]*?)\\\]/g;
const BACKSLASH_INLINE_MATH = /\\\(([\s\S]*?)\\\)/g;

/** A LaTeX command settles it: no amount of surrounding prose makes `\frac{a}{b}` not math. */
const LATEX_COMMAND = /\\[A-Za-z]+/;

/**
 * Signals that `$...$` content is an equation. Stricter than the backslash forms above, because a
 * single `$` is also a currency sign and `extractMathExpressions` pairs the first two on a line:
 * "Gas costs $3/gal. Milk costs $4/gal." yields the span `3/gal. Milk costs `, which KaTeX renders
 * happily (so the raw-text fallback never fires) and the speech engine reads out as "3 divided by
 * g of a l period M i l k c o s t s", swallowing the prose between the two prices. Prose words and
 * a dangling operator ("costs $5 + $3" spans `5 + `) are what separate those accidents from math.
 */
const DOLLAR_MATH_SIGNAL = /[=^_+*/<>]|[±×÷≤≥≠≈]/;
const PROSE_WORD = /[A-Za-z]{3,}/;
const DANGLING_OPERATOR = /[+\-*/=<>^_]\s*$/;

const isDollarMathContent = (content: string): boolean => {
  if (LATEX_COMMAND.test(content)) return true;
  return !PROSE_WORD.test(content) && !DANGLING_OPERATOR.test(content) && DOLLAR_MATH_SIGNAL.test(content);
};

export const replaceMathEquations = async (text: string) => {
  if (!textHasMathEquations(text)) {
    return text;
  }

  await _injectScript('https://cdn.jsdelivr.net/npm/speech-rule-engine@4.1.0/lib/sre.min.js');

  try {
    window.SRE.setupEngine({ domain: 'clearspeak' });
    await window.SRE.engineReady();
  } catch (error) {
    logger.error('Failed to setup SRE engine:', error);
    return text;
  }

  /** Speech for one expression, falling back to the original delimited text rather than dropping it. */
  const toSpeech = (expression: string, raw: string): string => {
    try {
      const rendered = renderMath({ expression, output: 'mathml' });
      const mathml = rendered.html;
      if (!rendered.success || !mathml) return raw;

      const match = mathml.match(/<math[^>]*>[\s\S]*?<\/math>/);
      if (!match) return raw;

      return window.SRE.toSpeech(match[0]) || raw;
    } catch (error) {
      logger.error('Failed to convert math equation:', error);
      return raw;
    }
  };

  const withBackslashMathSpoken = text
    .replace(BACKSLASH_DISPLAY_MATH, (raw, latex: string) => toSpeech(latex, raw))
    .replace(BACKSLASH_INLINE_MATH, (raw, latex: string) => toSpeech(latex, raw));

  // `$x$` / `$$x$$` — what Studio documents export (see the math specs in @magicschool/blocknote).
  return segmentDelimitedMath(withBackslashMathSpoken)
    .map((segment) => {
      if (segment.type === 'text') return segment.value;
      // `$$` is unambiguous authorship; a lone `$` has to earn it.
      if (segment.delimiter === '$' && !isDollarMathContent(segment.expression)) return segment.raw;
      return toSpeech(segment.expression, segment.raw);
    })
    .join('');
};
